// frontend/src/pages/admin/AdminEditVehiclePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vehiclesApi } from '../../api/vehicles';
import helpers from '../../utils/helpers';
import { FaEdit, FaSave, FaArrowLeft, FaSpinner, FaTags, FaCalculator } from 'react-icons/fa';

function AdminEditVehiclePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: '',
    price: '',
    discount_percentage: '', // <--- NEW FIELD
    condition: '',
    transmission: '',
    mileage: '',
    color: '',
    location_state: '',
    location_lga: '',
    description: '',
    features: ''
  });

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const data = await vehiclesApi.getVehicleById(id);
        setFormData({
          make: data.make,
          model: data.model,
          year: data.year,
          price: data.price,
          discount_percentage: data.discount_percentage || 0, // <--- Load existing discount
          condition: data.condition,
          transmission: data.transmission,
          mileage: data.mileage,
          color: data.color,
          location_state: data.location_state,
          location_lga: data.location_lga,
          description: data.description || '',
          features: data.features ? data.features.join(', ') : ''
        });
      } catch (err) {
        setError("Failed to load vehicle data.");
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        year: parseInt(formData.year),
        price: parseFloat(formData.price),
        discount_percentage: parseFloat(formData.discount_percentage) || 0, // <--- Send to API
        mileage: parseInt(formData.mileage),
        features: formData.features.split(',').map(f => f.trim()).filter(f => f !== '')
      };

      await vehiclesApi.updateListing(id, payload);
      alert("Vehicle updated successfully!");
      navigate('/admin/vehicles'); // Go back to Admin Vehicle list
    } catch (err) {
      console.error("Update error:", err);
      setError("Failed to update listing.");
    } finally {
      setSaving(false);
    }
  };

  // Helper to calculate final price for display
  const calculateFinalPrice = () => {
      const price = parseFloat(formData.price) || 0;
      const discount = parseFloat(formData.discount_percentage) || 0;
      if (discount <= 0) return price;
      return price - (price * (discount / 100));
  };

  if (loading) return <div className="text-center py-20"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading...</div>;

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4 max-w-3xl">
        <button onClick={() => navigate('/admin/vehicles')} className="text-gray-500 hover:text-primary mb-6 flex items-center gap-2">
          <FaArrowLeft /> Back to Listings
        </button>

        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <FaEdit className="text-primary" /> Edit Vehicle Listing
          </h1>

          {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* Identity Section (Read Only) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 bg-gray-50 p-4 rounded border border-gray-200">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Make</label>
                <input type="text" value={formData.make} className="w-full bg-transparent font-semibold text-gray-700" disabled />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Model</label>
                <input type="text" value={formData.model} className="w-full bg-transparent font-semibold text-gray-700" disabled />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Year</label>
                <input type="text" value={formData.year} className="w-full bg-transparent font-semibold text-gray-700" disabled />
              </div>
            </div>

            {/* Pricing & Discount Section */}
            <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-6">
                <h3 className="text-sm font-bold text-blue-800 mb-3 flex items-center gap-2">
                    <FaTags /> Pricing & Discounts
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Original Price (NGN)</label>
                        <input 
                            type="number" 
                            name="price" 
                            value={formData.price} 
                            onChange={handleChange} 
                            className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary" 
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                        <input 
                            type="number" 
                            name="discount_percentage" 
                            value={formData.discount_percentage} 
                            onChange={handleChange} 
                            min="0" 
                            max="100"
                            className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary" 
                            placeholder="0"
                        />
                    </div>
                    <div className="bg-white p-2 rounded border border-blue-200 text-right">
                        <span className="block text-xs text-gray-500 uppercase font-bold">Final Price</span>
                        <span className="text-lg font-bold text-green-600 flex items-center justify-end gap-2">
                            <FaCalculator className="text-gray-400 text-xs" />
                            {helpers.formatCurrency(calculateFinalPrice(), 'NGN')}
                        </span>
                    </div>
                </div>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mileage (km)</label>
                <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transmission</label>
                <select name="transmission" value={formData.transmission} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary bg-white">
                    <option value="automatic">Automatic</option>
                    <option value="manual">Manual</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                <input type="text" name="color" value={formData.color} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                <select name="condition" value={formData.condition} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary bg-white">
                    <option value="nigerian_used">Nigerian Used</option>
                    <option value="foreign_used">Foreign Used</option>
                    <option value="new">Brand New</option>
                </select>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Features (Comma separated)</label>
              <input type="text" name="features" value={formData.features} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary" />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea name="description" rows="4" value={formData.description} onChange={handleChange} className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary"></textarea>
            </div>

            <button 
              type="submit" 
              disabled={saving}
              className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg transition duration-300 flex items-center justify-center gap-2"
            >
              {saving ? 'Saving...' : <><FaSave /> Update Listing</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminEditVehiclePage;